import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { FaArrowLeft, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const AccountantSocPaymentDetailsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [monthYear, setMonthYear] = useState('');

  useEffect(() => {
    if (!userId) {
      navigate('/accountant/society-payment-list');
      return;
    }

    setLoading(true);
    const userRef = ref(db, `ekamwatapzp/SocietyRegistrations/${userId}`);

    onValue(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const user = snapshot.val();
        setUserData(user);

        const paymentNode = user.payments || {};
        const flattened = Object.entries(paymentNode).map(([type, p]) => {
          const amount = parseFloat(p.paidAmount || 0);
          const tenderFee = p.tenderFee ? parseFloat(p.tenderFee) : (amount / 1.18);
          const gst = p.gst ? parseFloat(p.gst) : (amount - tenderFee);

          return {
            id: type,
            type: type,
            date: p.date || 'N/A',
            transactionId: p.userUpiId || p.transactionId || p.receiptId || 'N/A',
            amount: amount.toFixed(2),
            tenderFee: tenderFee.toFixed(2),
            gst: gst.toFixed(2),
            upiId: p.userUpiId || 'N/A',
            status: p.status || 'N/A',
            rawDate: p.date ? new Date(p.date) : new Date(0)
          };
        });

        // Sort by date descending
        flattened.sort((a, b) => b.rawDate - a.rawDate);
        setPayments(flattened);
      }
      setLoading(false);
    });
  }, [userId, navigate]);

  const filteredPayments = payments.filter(p => {
    const searchMatch = p.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || 
                       p.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    let dateMatch = true;
    if (fromDate && toDate) {
      const d = new Date(p.date);
      const start = new Date(fromDate);
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      dateMatch = d >= start && d <= end;
    } else if (monthYear) {
      const [year, month] = monthYear.split('-');
      const d = new Date(p.date);
      dateMatch = d.getFullYear() === parseInt(year) && (d.getMonth() + 1) === parseInt(month);
    }

    return searchMatch && dateMatch;
  });

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(filteredPayments.map((p, i) => ({
      '#': i + 1,
      'Payment Date': p.date,
      'Society Name': userData?.societyName || 'N/A',
      'Mobile Number': userData?.mobile || userData?.whatsappNo || userData?.chairmanWhatsapp || 'N/A',
      'Transaction Id': p.transactionId,
      'Payment Type': p.type,
      'UPI ID': p.upiId,
      'Amount': p.amount,
      'Tender Fee': p.tenderFee,
      'GST': p.gst,
      'Status': p.status
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SocietyPayments");
    XLSX.writeFile(wb, `${userData?.societyName || 'Society'}_Payments.xlsx`);
  };

  const totals = filteredPayments.reduce((acc, p) => ({
    amount: acc.amount + parseFloat(p.amount),
    tenderFee: acc.tenderFee + parseFloat(p.tenderFee),
    gst: acc.gst + parseFloat(p.gst)
  }), { amount: 0, tenderFee: 0, gst: 0 });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[98%] mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header Section */}
        <div className="px-8 py-6 flex flex-col md:flex-row justify-between items-start md:items-center border-b border-gray-100 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Payment Details</h1>
            <div className="text-sm text-gray-500 font-medium mt-1">
              ठाणे जिल्हा परिषद / <span className="font-bold">Payment Details</span>
            </div>
          </div>
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold text-sm transition-all shadow-md active:scale-95"
          >
            <FaArrowLeft size={14} /> Back
          </button>
        </div>

        {/* Content Section */}
        <div className="p-6">
          {/* Society Info Card */}
          <div className="mb-8 p-6 bg-indigo-50/50 border border-indigo-100 rounded-2xl flex flex-wrap gap-8 items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-200">
                {userData?.societyName?.charAt(0) || 'S'}
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{userData?.societyName || 'N/A'}</h2>
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">मजूर सहकारी संस्था</p>
              </div>
            </div>
            <div className="flex gap-12">
              <div className="flex flex-col">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">GST Number</span>
                <span className="text-sm font-bold text-gray-700">{userData?.societyGst || userData?.gstNo || 'N/A'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Chairman</span>
                <span className="text-sm font-bold text-gray-700">{userData?.chairmanName || 'N/A'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Registration No</span>
                <span className="text-sm font-bold text-gray-700">{userData?.registrationNo || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-6 mb-8">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">From Date <span className="text-red-500">*</span></label>
              <input 
                type="date" 
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-gray-600 focus:outline-none focus:border-blue-500 w-48 shadow-sm transition-all"
                placeholder="dd-mm-yyyy"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">To Date <span className="text-red-500">*</span></label>
              <input 
                type="date" 
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-gray-600 focus:outline-none focus:border-blue-500 w-48 shadow-sm transition-all"
                placeholder="dd-mm-yyyy"
              />
            </div>
            <div className="flex items-center pb-2.5 text-red-600 font-bold px-2">
              OR
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Month & Year <span className="text-red-500">*</span></label>
              <input 
                type="month" 
                value={monthYear}
                onChange={(e) => setMonthYear(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-gray-600 focus:outline-none focus:border-blue-500 w-48 shadow-sm transition-all"
              />
            </div>
            <button className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-8 rounded transition-all shadow-md transform active:scale-95">
              Search
            </button>
          </div>

          {/* Secondary Actions: Search & Export */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <span className="text-sm font-bold text-gray-500 whitespace-nowrap">Search:</span>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 px-4 py-2 border border-gray-200 rounded text-sm font-medium focus:ring-2 focus:ring-indigo-500/20 outline-none transition bg-gray-50"
                placeholder="Transaction Id or Type..."
              />
            </div>
            <button 
              onClick={handleExport}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2 px-8 rounded transition-all shadow-md transform active:scale-95 text-sm flex items-center gap-2"
            >
              <FaFileExcel /> Download Excel
            </button>
          </div>

          {/* Table Container */}
          <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
            <table className="w-full text-left border-collapse min-w-[1000px]">
              <thead>
                <tr className="bg-white text-gray-800 text-xs font-bold uppercase tracking-wider">
                  <th className="px-4 py-4 border-b border-r border-gray-200 w-16">#</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200 whitespace-nowrap">Payment Date</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200 min-w-[200px]">Society Name</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200">Mobile Number</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200">Transaction Id</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200">Payment Type</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200">UPI ID</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200">Amount</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200">Tender Fee</th>
                  <th className="px-4 py-4 border-b border-r border-gray-200">GST</th>
                  <th className="px-4 py-4 border-b">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 italic">
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-12 text-center text-gray-400 font-bold bg-[#f8f9fa]">
                      Loading data...
                    </td>
                  </tr>
                ) : filteredPayments.length > 0 ? (
                  filteredPayments.map((p, index) => (
                    <tr key={index} className="hover:bg-indigo-50/20 transition-all font-medium text-gray-700">
                      <td className="px-4 py-4 border-r border-gray-100 text-center text-sm font-bold">{index + 1}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-gray-500 whitespace-nowrap">{p.date}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-gray-800">{userData?.societyName || 'N/A'}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-gray-600 truncate">{userData?.mobile || userData?.whatsappNo || userData?.chairmanWhatsapp || 'N/A'}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-blue-600">{p.transactionId}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-xs font-bold whitespace-nowrap">
                        <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-lg">{p.type}</span>
                      </td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-gray-600">{p.upiId}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-gray-800">{p.amount}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-gray-600">{p.tenderFee}</td>
                      <td className="px-4 py-4 border-r border-gray-100 text-sm font-bold text-gray-600">{p.gst}</td>
                      <td className="px-4 py-4 text-xs font-black text-center">
                        <span className={`px-3 py-1 rounded-full border ${
                          p.status === 'success' || p.status === 'Completed' ? 'bg-green-50 text-green-600 border-green-100' : 
                          p.status === 'pending' || p.status === 'Pending' ? 'bg-amber-50 text-amber-600 border-amber-100' : 
                          'bg-red-50 text-red-600 border-red-100'
                        }`}>
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="px-4 py-12 text-center text-gray-400 font-bold bg-[#f8f9fa]">
                      No payment records found.
                    </td>
                  </tr>
                )}
                {/* Grand Total Row */}
                <tr className="bg-slate-50 font-bold text-gray-900 border-t border-gray-300">
                  <td className="px-4 py-5 border-r border-gray-200" colSpan={7}>Grand Total</td>
                  <td className="px-4 py-5 border-r border-gray-200">{totals.amount.toFixed(2)}</td>
                  <td className="px-4 py-5 border-r border-gray-200">{totals.tenderFee.toFixed(2)}</td>
                  <td className="px-4 py-5 font-bold" colSpan={2}>{totals.gst.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountantSocPaymentDetailsList;
